<template>
  <div class="customer-category-tree-config">
    <div class="page-bar">
      <div class="page-bar__context">
        <div class="context-item">
          <span class="context-item__label">品牌：</span>
          <a-select
            v-model:value="state.searchParams.brandId"
            style="width: 120px"
            @change="state.onContextChange"
          >
            <a-select-option
              v-for="item in state.brands"
              :key="item.id"
              :value="item.id"
            >
              {{ item.name }}
            </a-select-option>
          </a-select>
        </div>
        <div class="context-item">
          <span class="context-item__label">渠道：</span>
          <a-select
            v-model:value="state.searchParams.channelId"
            style="width: 120px"
            @change="state.onContextChange"
          >
            <a-select-option
              v-for="item in state.channels"
              :key="item.id"
              :value="item.id"
            >
              {{ item.name }}
            </a-select-option>
          </a-select>
        </div>
        <div class="context-item">
          <span class="context-item__label">站点：</span>
          <a-select
            v-model:value="state.searchParams.siteId"
            style="width: 120px"
            @change="state.onContextChange"
          >
            <a-select-option
              v-for="item in state.sites"
              :key="item.id"
              :value="item.id"
            >
              {{ item.name }}
            </a-select-option>
          </a-select>
        </div>
      </div>
      <div class="page-bar__actions">
        <a-button class="mr10" @click="state.openImportModal">
          导入
        </a-button>
        <a-button type="primary" @click="state.openCreateModal">
          + 新建配置
        </a-button>
      </div>
    </div>

    <div class="ims-container">
      <div class="filter-bar">
        <div class="filter-bar__left">
          <span class="filter-bar__label">状态：</span>
          <a-select
            v-model:value="state.filterParams.status"
            style="width: 120px"
            class="mr10"
          >
            <a-select-option value="0">全部</a-select-option>
            <a-select-option value="1">已被映射引用</a-select-option>
            <a-select-option value="2">未被引用</a-select-option>
          </a-select>
          <a-input
            :value="state.filterParams.keyword"
            style="width: 220px"
            placeholder="搜索类目树名称..."
            allow-clear
            @update:value="state.handleKeywordChange"
            @pressEnter="state.handleSearch"
          />
        </div>
        <div class="filter-bar__right">
          <a-button type="primary" class="mr10" @click="state.handleSearch">
            查询
          </a-button>
          <a-button @click="state.handleReset">重置</a-button>
        </div>
      </div>

      <a-table
        :dataSource="state.dataSource"
        :columns="state.columns"
        :loading="state.loading"
        :pagination="false"
        row-key="id"
        bordered
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'index'">
            {{ record.id }}
          </template>
          <template v-else-if="column.dataIndex === 'treeName'">
            <div class="tree-name-cell">
              <span
                class="tree-name-cell__text"
                :title="record.treeName"
                @dblclick.stop="state.openEditModal(record)"
              >
                {{ record.treeName || '-' }}
              </span>
            </div>
          </template>
          <template v-else-if="column.dataIndex === 'mappingLabel'">
            <a-tooltip
              v-if="record.mappingTags && record.mappingTags.length"
              placement="top"
              :overlay-style="{ maxWidth: '400px' }"
            >
              <template #title>
                <div style="display: flex; flex-wrap: wrap; gap: 6px">
                  <a-tag
                    v-for="(tag, tagIndex) in record.mappingTags"
                    :key="`${tag}-${tagIndex}`"
                    color="purple"
                    style="margin: 0"
                  >
                    {{ tag }}
                  </a-tag>
                </div>
              </template>
              <div class="mapping-tags-cell">
                <a-tag
                  v-for="(tag, tagIndex) in record.mappingTags.slice(0, 3)"
                  :key="`${tag}-${tagIndex}`"
                  class="mapping-tags-cell__tag"
                  color="purple"
                >
                  {{ tag }}
                </a-tag>
                <span
                  v-if="record.mappingTags.length > 3"
                  class="mapping-tags-cell__more"
                >
                  ...
                </span>
              </div>
            </a-tooltip>
            <span v-else>-</span>
          </template>
          <template v-else-if="column.dataIndex === 'nodeCount'">
            L1: {{ record.levelCounts[0] || 0 }} / L2:
            {{ record.levelCounts[1] || 0 }} / L3:
            {{ record.levelCounts[2] || 0 }}
          </template>
          <template v-else-if="column.dataIndex === 'action'">
            <a class="mr10" @click="state.goEdit(record)">编辑</a>
            <a
              class="mr10"
              :class="{ 'action-disabled': state.exportingId === record.id }"
              @click="state.exportRow(record)"
            >
              {{ state.exportingId === record.id ? '导出中...' : '导出' }}
            </a>
            <a-tooltip
              v-if="record.referenced"
              title="该类目树存在被引用映射，无法删除"
            >
              <span class="action-disabled">删除</span>
            </a-tooltip>
            <a-popconfirm
              v-else
              title="确认删除该类目树吗？"
              ok-text="确认"
              cancel-text="取消"
              @confirm="state.removeRow(record)"
            >
              <a style="color: #ff4d4f">删除</a>
            </a-popconfirm>
            <a class="ml10" @click="state.openCopyModal(record)">复制</a>
          </template>
        </template>
      </a-table>

      <div v-if="state.pagination.total > 0" class="table-footer">
        <a-pagination
          :current="state.pagination.pageNum"
          :page-size="state.pagination.pageSize"
          :total="state.pagination.total"
          show-size-changer
          show-quick-jumper
          :show-total="total => `共 ${total} 条`"
          @change="state.onPageChange"
        />
      </div>
    </div>

    <a-modal
      v-model:visible="state.createVisible"
      title="新建客类目树"
      :footer="null"
      @cancel="state.closeCreateModal"
    >
      <a-form layout="vertical">
        <a-form-item label="类目树名称">
          <a-input
            v-model:value="state.createForm.treeName"
            placeholder="如：夏季实验树、大促专用树"
          />
        </a-form-item>
        <a-form-item label="描述">
          <a-textarea
            v-model:value="state.createForm.description"
            :rows="4"
            placeholder="选填，方便团队识别用途"
          />
        </a-form-item>
      </a-form>
      <div class="modal-footer">
        <a-button @click="state.closeCreateModal">取消</a-button>
        <a-button type="primary" @click="state.confirmCreate">
          创建
        </a-button>
      </div>
    </a-modal>

    <a-modal
      v-model:visible="state.copyVisible"
      title="复制对客类目树"
      :footer="null"
      :mask-closable="!state.copying"
      @cancel="state.closeCopyModal"
    >
      <a-form layout="vertical">
        <a-form-item label="复制后的类目树名称" required>
          <a-input
            v-model:value="state.copyForm.treeName"
            :maxlength="100"
            placeholder="请输入复制后的类目树名称"
            @pressEnter="state.confirmCopy"
          />
        </a-form-item>
      </a-form>
      <div class="modal-footer">
        <a-button :disabled="state.copying" @click="state.closeCopyModal">
          取消
        </a-button>
        <a-button
          type="primary"
          :loading="state.copying"
          @click="state.confirmCopy"
        >
          确认复制
        </a-button>
      </div>
    </a-modal>

    <a-modal
      v-model:visible="state.editVisible"
      title="编辑类目树"
      :footer="null"
      @cancel="state.closeEditModal"
    >
      <a-form layout="vertical">
        <a-form-item label="类目树名称">
          <a-input
            v-model:value="state.editForm.treeName"
            :maxlength="100"
            placeholder="请输入类目树名称"
          />
        </a-form-item>
        <a-form-item label="描述">
          <a-textarea
            v-model:value="state.editForm.description"
            :rows="4"
            placeholder="选填，方便团队识别用途"
          />
        </a-form-item>
      </a-form>
      <div class="modal-footer modal-footer--right">
        <a-button :disabled="state.renaming" @click="state.closeEditModal">
          取消
        </a-button>
        <a-button
          type="primary"
          :loading="state.renaming"
          @click="state.confirmEdit"
        >
          保存
        </a-button>
      </div>
    </a-modal>

    <a-modal
      v-model:visible="state.importVisible"
      title="导入对客类目树"
      :footer="null"
      :width="560"
      :mask-closable="!state.importing"
      @cancel="state.closeImportModal"
    >
      <div class="tree-import">
        <a-alert
          class="tree-import__alert"
          type="info"
          show-icon
          message="请先下载 Excel 模板，按模板填写类目数据后导入。"
        />
        <a-form layout="vertical">
          <a-form-item label="类目树名称" required>
            <a-input
              v-model:value="state.importForm.treeName"
              :maxlength="100"
              placeholder="请输入类目树名称"
            />
          </a-form-item>
          <a-form-item label="描述">
            <a-textarea
              v-model:value="state.importForm.description"
              :rows="3"
              placeholder="选填，方便团队识别用途"
            />
          </a-form-item>
          <a-form-item label="Excel 文件" required>
            <div class="tree-import__upload-header">
              <span>请上传按模板填写的文件</span>
              <a-button
                type="link"
                size="small"
                :loading="state.downloadingTemplate"
                @click="state.downloadTemplate"
              >
                下载模板
              </a-button>
            </div>
            <a-upload
              :before-upload="() => false"
              :file-list="state.importFileList"
              :max-count="1"
              accept=".xlsx,.xls"
              @change="state.handleImportFileChange"
            >
              <a-button>选择 Excel 文件</a-button>
            </a-upload>
            <div class="tree-import__hint">仅支持 `.xlsx`、`.xls` 格式。</div>
          </a-form-item>
        </a-form>

        <div class="modal-footer">
          <a-button :disabled="state.importing" @click="state.closeImportModal">
            取消
          </a-button>
          <a-button
            type="primary"
            :loading="state.importing"
            @click="state.confirmImport"
          >
            确认导入
          </a-button>
        </div>
      </div>
    </a-modal>

    <a-modal
      v-model:visible="state.importErrorVisible"
      title="导入校验失败"
      :width="680"
      ok-text="我知道了"
      :cancel-button-props="{ style: { display: 'none' } }"
      @ok="state.importErrorVisible = false"
    >
      <a-alert
        class="tree-import__error-summary"
        type="error"
        show-icon
        :message="
          `共发现 ${state.importErrors.length} 条错误，请修改 Excel 后重新导入。`
        "
      />
      <a-table
        row-key="errorKey"
        size="small"
        bordered
        :columns="state.importErrorColumns"
        :data-source="state.importErrors"
        :pagination="false"
        :scroll="{ y: 360 }"
      />
    </a-modal>
  </div>
</template>

<script>
import { onMounted, reactive, toRefs } from 'vue'
import { useRouter } from 'vue-router'
import useAPI from 'UseApi'
import { message } from 'ant-design-vue'
import { postMessageToParent } from 'cupshe-utils'
import {
  getCustomerCategoryContext,
  setCustomerCategoryContext
} from '@/utils/customerCategoryContextCache'
import {
  listCustomerCategoryTrees,
  copyCustomerCategoryTree,
  createCustomerCategoryTree,
  deleteCustomerCategoryTree,
  downloadCustomerCategoryTreeImportTemplate,
  exportCustomerCategoryTree,
  importCustomerCategoryTree,
  updateCustomerCategoryTree
} from './service'

const { common_querySiteInfo_get_ } = useAPI(null, [3426])

const getRequestErrorMessage = (error, fallback) =>
  error?.retInfo ||
  error?.response?.data?.retInfo ||
  error?.data?.retInfo ||
  error?.message ||
  fallback

export default {
  name: 'CustomerCategoryTreeConfig',
  setup() {
    const router = useRouter()
    const state = reactive({
      columns: [
        { title: '序号', dataIndex: 'index', width: 70, align: 'center' },
        { title: '类目树名称', dataIndex: 'treeName', align: 'center' },
        {
          title: '被引用映射',
          dataIndex: 'mappingLabel',
          width: 360,
          align: 'center'
        },
        {
          title: '节点数',
          dataIndex: 'nodeCount',
          width: 210,
          align: 'center'
        },
        {
          title: '更新时间',
          dataIndex: 'updateTime',
          width: 180,
          align: 'center'
        },
        { title: '操作项', dataIndex: 'action', width: 280, align: 'center' }
      ],
      dataSource: [],
      loading: false,
      brands: [],
      channels: [],
      sites: [],
      terminals: [
        { id: 1, name: 'WEB' },
        { id: 2, name: 'APP' }
      ],
      searchParams: getCustomerCategoryContext(),
      filterParams: {
        status: '0',
        keyword: ''
      },
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      createVisible: false,
      copyVisible: false,
      copyRecord: null,
      copyForm: { treeName: '' },
      copying: false,
      editVisible: false,
      editRecord: null,
      editForm: {
        treeName: '',
        description: ''
      },
      renaming: false,
      createForm: {
        treeName: '',
        description: ''
      },
      exportingId: '',
      importVisible: false,
      importing: false,
      importErrorVisible: false,
      importErrors: [],
      importErrorColumns: [
        {
          title: 'Excel 行号',
          dataIndex: 'rowNumber',
          width: 100,
          align: 'center'
        },
        { title: '字段', dataIndex: 'field', width: 160 },
        { title: '错误原因', dataIndex: 'message' }
      ],
      downloadingTemplate: false,
      importForm: {
        treeName: '',
        description: ''
      },
      importFileList: [],
      initList: async () => {
        state.loading = true
        try {
          const { list, total } = await listCustomerCategoryTrees({
            ...state.searchParams,
            ...state.filterParams,
            ...state.pagination
          })
          state.dataSource = list
          state.pagination.total = total
        } catch (error) {
          message.error(error.message || '类目树列表加载失败')
        } finally {
          state.loading = false
        }
      },
      onContextChange: () => {
        setCustomerCategoryContext(state.searchParams)
        state.pagination.pageNum = 1
        state.initList()
      },
      handleSearch: () => {
        state.pagination.pageNum = 1
        state.initList()
      },
      handleKeywordChange: value => {
        state.filterParams.keyword = value
        if (value === '') {
          state.pagination.pageNum = 1
          state.initList()
        }
      },
      handleReset: () => {
        state.filterParams = {
          status: '0',
          keyword: ''
        }
        state.pagination.pageNum = 1
        state.initList()
      },
      onPageChange: (page, pageSize) => {
        if (pageSize !== state.pagination.pageSize) {
          state.pagination.pageNum = 1
        } else {
          state.pagination.pageNum = page
        }
        state.pagination.pageSize = pageSize
        state.initList()
      },
      openEditModal: record => {
        state.editRecord = record
        state.editForm = {
          treeName: record.treeName || '',
          description: record.description || ''
        }
        state.editVisible = true
      },
      closeEditModal: () => {
        if (state.renaming) return
        state.editVisible = false
        state.editRecord = null
        state.editForm = {
          treeName: '',
          description: ''
        }
      },
      confirmEdit: async () => {
        if (!state.editRecord || state.renaming) return
        const nextTreeName = `${state.editForm.treeName || ''}`.trim()
        const nextDescription = `${state.editForm.description || ''}`.trim()
        if (!nextTreeName) {
          message.error('类目树名称不能为空')
          return
        }
        if (
          nextTreeName === `${state.editRecord.treeName || ''}`.trim() &&
          nextDescription === `${state.editRecord.description || ''}`.trim()
        ) {
          state.closeEditModal()
          return
        }
        state.renaming = true
        try {
          await updateCustomerCategoryTree(state.editRecord.id, {
            treeName: nextTreeName,
            description: nextDescription
          })
          message.success('类目树信息已更新')
          state.closeEditModal()
          await state.initList()
        } catch (error) {
          message.error(error.message || '类目树信息更新失败')
        } finally {
          state.renaming = false
        }
      },
      openCreateModal: () => {
        state.createForm = {
          treeName: '',
          description: ''
        }
        state.createVisible = true
      },
      closeCreateModal: () => {
        state.createVisible = false
      },
      confirmCreate: async () => {
        if (!state.createForm.treeName) {
          message.error('请输入类目树名称')
          return
        }
        try {
          const created = await createCustomerCategoryTree({
            ...state.searchParams,
            ...state.createForm,
            createMode: 'blank'
          })
          message.success('类目树创建成功')
          state.createVisible = false
          await state.initList()
          router.push({
            name: 'CustomerCategoryTreeConfigDetail',
            query: {
              id: created.id,
              ...state.searchParams
            }
          })
        } catch (error) {
          message.error(error.message || '类目树创建失败')
        }
      },
      openCopyModal: record => {
        state.copyRecord = record
        state.copyForm = {
          treeName: `复制-${record.treeName || ''}`.slice(0, 100)
        }
        state.copyVisible = true
      },
      closeCopyModal: () => {
        if (state.copying) return
        state.copyVisible = false
        state.copyRecord = null
        state.copyForm = { treeName: '' }
      },
      confirmCopy: async () => {
        if (!state.copyRecord || state.copying) return
        const treeName = `${state.copyForm.treeName || ''}`.trim()
        if (!treeName) {
          message.error('请输入复制后的类目树名称')
          return
        }
        state.copying = true
        try {
          await copyCustomerCategoryTree(state.copyRecord.id, treeName)
          message.success('类目树复制成功')
          state.copyVisible = false
          state.copyRecord = null
          state.copyForm = { treeName: '' }
          await state.initList()
        } catch (error) {
          message.error(error.message || '类目树复制失败')
        } finally {
          state.copying = false
        }
      },
      goEdit: record => {
        router.push({
          name: 'CustomerCategoryTreeConfigDetail',
          query: {
            id: record.id,
            ...state.searchParams
          }
        })
      },
      removeRow: async record => {
        if (record.referenced) {
          message.warning('该类目树存在被引用映射，无法删除')
          return
        }
        try {
          await deleteCustomerCategoryTree(record.id)
          message.success('删除成功')
          if (
            state.dataSource.length === 1 &&
            state.pagination.pageNum > 1 &&
            state.pagination.total > 1
          ) {
            state.pagination.pageNum--
          }
          await state.initList()
        } catch (error) {
          message.error(error.message || '删除失败')
        }
      },
      exportRow: async record => {
        if (state.exportingId) return
        state.exportingId = record.id
        try {
          await exportCustomerCategoryTree(record.id)
          postMessageToParent({
            type: 'exportNum',
            value: ''
          })
          message.success('导出成功，请前往右上角下载中心下载')
        } catch (error) {
          message.error(error.message || '导出任务提交失败')
        } finally {
          state.exportingId = ''
        }
      },
      openImportModal: () => {
        state.importForm = {
          treeName: '',
          description: ''
        }
        state.importFileList = []
        state.importErrors = []
        state.importVisible = true
      },
      closeImportModal: () => {
        if (state.importing) return
        state.importFileList = []
        state.importVisible = false
      },
      handleImportFileChange: ({ fileList }) => {
        state.importFileList = fileList.slice(-1)
      },
      downloadTemplate: async () => {
        if (state.downloadingTemplate) return
        state.downloadingTemplate = true
        try {
          await downloadCustomerCategoryTreeImportTemplate(state.searchParams)
          message.success('模板下载成功')
        } catch (error) {
          message.error(error.message || '模板下载失败')
        } finally {
          state.downloadingTemplate = false
        }
      },
      confirmImport: async () => {
        if (state.importing) return
        const treeName = `${state.importForm.treeName || ''}`.trim()
        if (!treeName) {
          message.error('请输入类目树名称')
          return
        }
        const uploadFile = state.importFileList[0]
        const file = uploadFile?.originFileObj || uploadFile
        if (!file) {
          message.error('请选择要导入的 Excel 文件')
          return
        }
        if (!/\.xlsx?$/i.test(file.name || '')) {
          message.error('仅支持 .xlsx、.xls 格式')
          return
        }
        state.importing = true
        try {
          const result = await importCustomerCategoryTree({
            ...state.searchParams,
            ...state.importForm,
            treeName,
            file
          })
          const errors = Array.isArray(result?.errors) ? result.errors : []
          if (errors.length) {
            state.importErrors = errors.map((item, index) => ({
              ...item,
              rowNumber: item.rowNumber ?? '-',
              field: item.field || '-',
              message: item.message || '未知错误',
              errorKey: `${item.rowNumber ?? 'unknown'}-${item.field ||
                'field'}-${index}`
            }))
            state.importErrorVisible = true
            message.error(`导入校验失败，共 ${errors.length} 条错误`)
            return
          }
          message.success('导入成功')
          state.importVisible = false
          state.importFileList = []
          await state.initList()
        } catch (error) {
          message.error(
            getRequestErrorMessage(error, '导入失败，请检查文件内容')
          )
        } finally {
          state.importing = false
        }
      }
    })

    onMounted(async () => {
      await common_querySiteInfo_get_({ state })
      if (!state.terminals.length) {
        state.terminals = [
          { id: 1, name: 'WEB' },
          { id: 2, name: 'APP' }
        ]
      }
      state.initList()
    })

    return {
      ...toRefs(state),
      state
    }
  }
}
</script>

<style lang="scss" scoped>
.customer-category-tree-config {
  .page-bar {
    background: #ffffff;
    padding: 10px 20px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__context,
    &__actions {
      display: flex;
      align-items: center;
    }
  }

  .context-item {
    display: flex;
    align-items: center;
    margin-right: 20px;

    &__label {
      white-space: nowrap;
    }
  }

  .tree-name-cell {
    min-height: 24px;

    &__text {
      display: inline-block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: pointer;
    }
  }

  .mapping-tags-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    max-width: 340px;
    margin: 0 auto;
    overflow: hidden;

    &__tag {
      min-width: 0;
      max-width: 96px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__more {
      flex: none;
      color: #8c8c8c;
    }
  }

  .action-disabled {
    color: #bfbfbf;
    cursor: not-allowed;
  }

  .filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    &__left,
    &__right {
      display: flex;
      align-items: center;
    }

    &__label {
      margin-right: 8px;
      white-space: nowrap;
    }
  }

  .table-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;

  &--right {
    justify-content: flex-end !important;
  }
}

.tree-import {
  &__alert {
    margin-bottom: 20px;
  }

  &__upload-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__hint {
    margin-top: 8px;
    color: #999;
    font-size: 12px;
  }

  &__error-summary {
    margin-bottom: 16px;
  }
}

.ml10 {
  margin-left: 10px;
}
</style>
